import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchSuggestions, typeLabels } from "@/utils/media";

interface SearchBoxProps {
  variant?: "desktop" | "mobile";
  placeholder?: string;
  initialValue?: string;
  onSubmitted?: () => void;
}

export default function SearchBox({
  variant = "desktop",
  placeholder = "搜索电影、电视剧、综艺或演员…",
  initialValue = "",
  onSubmitted,
}: SearchBoxProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const suggestions = searchSuggestions(value, 6);

  const submit = () => {
    const keyword = value.trim();
    if (!keyword) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setFocused(false);
    onSubmitted?.();
  };

  return (
    <div className="relative w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="w-full"
      >
        <div
          className={`flex items-center gap-2 rounded-md border bg-background-50 px-3 transition-colors ${
            focused ? "border-primary-400" : "border-background-300"
          } ${variant === "mobile" ? "h-11" : "h-10"}`}
        >
          <i className="ri-search-line text-lg text-foreground-400"></i>
          <input
            type="search"
            value={value}
            placeholder={placeholder}
            aria-label="站内搜索"
            onChange={(event) => setValue(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => window.setTimeout(() => setFocused(false), 150)}
            className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="hidden shrink-0 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 sm:block"
          >
            搜索
          </button>
        </div>
      </form>

      {focused && value.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-md border border-background-200 bg-background-50 shadow-lg">
          {suggestions.length > 0 ? (
            <ul className="max-h-80 overflow-y-auto py-1">
              {suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onMouseDown={() => {
                      navigate(`/detail/${item.id}`);
                      setFocused(false);
                      onSubmitted?.();
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="h-11 w-8 shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={item.poster}
                        alt={item.title}
                        title={`${item.title} 影视海报`}
                        className="h-full w-full object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900">
                        {item.title}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {typeLabels[item.type]} · {item.year} · {item.region}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-4 text-center text-xs text-foreground-500">
              未找到与「{value.trim()}」相关的影视内容
            </div>
          )}
        </div>
      )}
    </div>
  );
}